import { Anchor, Box, Button, Skeleton, Table, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { CalendarPlusIcon, FilePdfIcon, FileTextIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSwr, { mutate as globalMutate } from "swr";
import { SettlementApi, SettlementInvoiceApi } from "../../apis";
import { ExtendPaymentModal } from "../../components/invoice-actions/extend-payment-modal";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import { Page, PageHeader } from "../../components/layout/Page";
import { ListToolbar, RowActions } from "../../components/list";
import { usePermission } from "../../components/permission";
import { InvoicePeekDrawer, peekKey } from "../../components/settlement/invoice-peek";
import { SettlementLoadError } from "../../components/settlement/load-error";
import grid from "../../components/ui/data-grid.module.css";
import { StatusBadge } from "../../components/ui/status-badge";
import { usePdfDownload } from "../../hooks/usePdfDownload";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { ubFormat } from "../../utils/ub-time";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";
import classes from "./SettlementDetails.module.css";

const TABS = ["all", "add", "sub", "returned", "overdue"] as const;
type Tab = (typeof TABS)[number];
const STATUS_COLORS: Record<string, string> = { NONE: "gray", SENT: "gray", CONFIRMED: "green" };
const KIND_COLORS: Record<string, string> = {
  CONFIRMED: "gray",
  PAYMENT: "green",
  RETURNED: "yellow",
  RETURN: "yellow",
  CREDIT_NOTE: "yellow",
  CANCELLED: "red",
  CANCELED: "red",
  AMENDMENT: "yellow",
  AMENDED: "yellow",
  DEBIT_NOTE: "gray",
  LATE_FEE: "gray",
};
const COLUMN_COUNT = 9;

const readTab = (params: URLSearchParams): Tab => {
  const v = params.get("tab") as Tab | null;
  return v && (TABS as readonly string[]).includes(v) && v !== "all" ? v : "all";
};

const initials = (name: string) => Array.from(name.trim()).slice(0, 2).join("").toUpperCase() || "-";

/**
 * Хуучин «Тооцоо нийлэх» (settlement) мөр. `readOnly` үед илгээх/зөвшөөрөх үйлдэл харагдахгүй:
 * шинэ урсгал нь тооцооны хуудас (/settlement/:id), хуучин мөр зөвхөн унших.
 */
export const SettlementDetails = ({ readOnly = false }: { readOnly?: boolean }) => {
  const { t } = useTranslation(["settlement", "common", "invoice"]);
  const params = useParams();
  const id = params.id;
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as any) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTab(searchParams);
  const openInvoiceId = searchParams.get("inv");
  const myBusinessType = useSelector((s: { auth: IAuth }) => s.auth.myBusinessType);

  const INV_SETTLEMENT_GET = usePermission("INV_SETTLEMENT_GET");
  const INV_SETTLEMENT_PAYMENT_PDF = usePermission("INV_SETTLEMENT_PAYMENT_PDF");
  const INV_SETT_SEND = usePermission("INV_SETT_SEND");
  const INV_SETTLEMENT_ACCEPT_PAYMENT = usePermission("INV_SETTLEMENT_ACCEPT_PAYMENT");
  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: SettlementApi.pdf });

  const [busy, setBusy] = useState(false);
  const [extendId, setExtendId] = useState("");
  const [extendOpened, extendHandlers] = useDisclosure(false);

  const {
    data,
    error: getError,
    mutate: mutateGet,
  } = useSwr<any>(
    id && INV_SETTLEMENT_GET.hasAccess ? `/inv/settlement/${id}` : null,
    async () => await SettlementApi.get(`${id}`),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const {
    data: stmt,
    error: stmtError,
    mutate: mutateStmt,
  } = useSwr<any>(
    id && INV_SETTLEMENT_GET.hasAccess ? `inv.settlement.statement.${id}.${tab}` : null,
    async () =>
      await SettlementInvoiceApi.list({
        offset: { page: 1, limit: 500 },
        // Хуучин backend үл мэдэх түлхүүрийг 400-аар татгалздаг тул «бүгд» үед илгээхгүй.
        filter: { settlementId: id, ...(tab !== "all" ? { type: tab } : {}) },
      }),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const reload = () => {
    void mutateGet();
    void mutateStmt();
  };

  const settlement = data?.settlement ?? {};
  const refCode: string | undefined = settlement.refCode ?? state.refCode;
  const status: string | undefined = settlement.settlementStatus ?? state.settlementStatus;
  const createdAt = settlement.createdAt ?? state.createdAt;
  const sentDate = settlement.sentDate ?? state.sentDate;
  const confirmedDate = settlement.confirmedDate ?? state.confirmedDate;
  const month: string | undefined = settlement.month ?? state.month;
  const periodStart = settlement.periodStart ?? (month ? `${month}-01` : undefined);
  const periodEnd = settlement.periodEnd ?? (month ? dayjs(`${month}-01`).endOf("month").format("YYYY-MM-DD") : undefined);
  const business = data?.business;
  const dash = t("common:list.dash");
  const loadedHeader = !!data;
  const showError = !!getError || !!stmtError;

  const rows: any[] = useMemo(() => (Array.isArray(stmt?.rows) ? stmt.rows : []), [stmt]);
  const counts = stmt?.counts as { all: number; add: number; sub: number; returned: number; overdue: number } | undefined;
  const stmtLoading = !stmt && !stmtError;

  const money = (v?: number | null) => (v == null ? dash : currencyFormat(Number(v), { withSymbol: false }));

  const onSend = async () => {
    setBusy(true);
    try {
      await SettlementApi.send(`${id}`);
      message.success(t("success.action"));
      navigate(-1);
    } catch (error) {
      handleError(error);
      setBusy(false);
    }
  };

  const onRespond = async () => {
    setBusy(true);
    try {
      await SettlementApi.respond(`${id}`);
      message.success(t("success.action"));
      navigate(-1);
    } catch (error) {
      handleError(error);
      setBusy(false);
    }
  };

  const setTab = useCallback(
    (next: string) => {
      const p = new URLSearchParams();
      if (next !== "all") p.set("tab", next);
      setSearchParams(p, { replace: true });
    },
    [setSearchParams],
  );

  const openInvoice = (invoiceId: string) => {
    const p = new URLSearchParams(searchParams);
    p.set("inv", invoiceId);
    setSearchParams(p);
  };
  const closeInvoice = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("inv");
    setSearchParams(p, { replace: true });
  };
  const openExtend = (invoiceId: string) => {
    setExtendId(invoiceId);
    extendHandlers.open();
  };
  const onExtended = () => {
    reload();
    void globalMutate(peekKey(extendId));
  };

  // Явц: NONE → Үүссэн, SENT → Илгээсэн, CONFIRMED → бүгд дууссан.
  const currentStep = status === "CONFIRMED" ? 3 : status === "SENT" ? 1 : 0;
  const steps = [
    { title: t("detail.step.created"), sub: ubFormat(createdAt, "MM-DD HH:mm") },
    { title: t("detail.step.sent"), sub: sentDate ? ubFormat(sentDate, "MM-DD HH:mm") : dash },
    {
      title: t("detail.step.confirm"),
      sub: confirmedDate ? ubFormat(confirmedDate, "MM-DD HH:mm") : t("detail.step.pending"),
    },
  ];
  const stepState = (i: number) => (i < currentStep ? "done" : i === currentStep ? "now" : "todo");

  const isSupplier = myBusinessType === "SUPPLIER";
  const action = readOnly
    ? status === "CONFIRMED" ? "pdf" : null
    : status === "NONE" && isSupplier ? "send" : status === "SENT" && myBusinessType === "BUYER" ? "respond" : status === "CONFIRMED" ? "pdf" : null;

  const invoiceLink = (invoiceId: string) => (isSupplier ? `/sales-invoice/${invoiceId}/details` : `/buyer-invoice/${invoiceId}/details`);

  const flow = [
    { label: t("detail.flow.opening"), value: money(data?.firstAmount), hint: t("detail.flow.asOf", { date: periodStart ?? dash }) },
    {
      label: `+ ${t("detail.flow.invoiced")}`,
      value: money(data?.confirmedAmount),
      hint: data?.confirmedInvCount != null ? t("detail.flow.invoiceCount", { count: data.confirmedInvCount }) : " ",
    },
    {
      label: `− ${t("detail.flow.paid")}`,
      value: money(data?.paidAmount),
      hint: data?.paidInvCount != null ? t("detail.flow.paymentCount", { count: data.paidInvCount }) : " ",
    },
    {
      label: `− ${t("detail.flow.returned")}`,
      value: money(data?.returnedAmount ?? 0),
      hint: data && !Number(data.returnedAmount) ? t("detail.flow.noReturns") : " ",
      muted: !Number(data?.returnedAmount),
    },
    {
      label: `= ${t("detail.flow.closing")}`,
      value: data?.lastAmount != null ? currencyFormat(Number(data.lastAmount), { withSymbol: true }) : dash,
      hint: t("detail.flow.asOf", { date: periodEnd ?? dash }),
      total: true,
    },
  ];

  const invoiceStatusCell = (r: any) => {
    if (r.direction !== "ADD") return null;
    const closed = r.invoice?.paymentStatus === "CLOSED" || r.invoice?.invoiceStatus === "CLOSED";
    if (closed) {
      return (
        <Box component="span" className={classes.statusCell}>
          <Text span className={`${classes.pill} ${classes.pillNeutral}`}>{t("detail.inv.closed")}</Text>
          <Text span className={classes.statusSub}>{t("detail.inv.fullyPaid")}</Text>
        </Box>
      );
    }
    const days = r.overdueDays;
    const sub =
      r.invoicePaymentStatus === "PENDING" || Number(r.invoiceAmountToPay) === Number(r.invoice?.confirmedAmount)
        ? t("detail.inv.unpaid")
        : t("detail.inv.left", { amount: money(r.invoiceAmountToPay) });
    let pill: { cls: string; text: string } | null = null;
    if (days != null && days > 0) pill = { cls: classes.pillDanger, text: t("detail.inv.overdue", { count: days }) };
    else if (days != null && days < 0) pill = { cls: classes.pillNeutral, text: t("detail.inv.remaining", { count: -days }) };
    else if (days === 0) pill = { cls: classes.pillWarn, text: t("detail.inv.dueToday") };
    if (!pill) return null;
    return (
      <Box component="span" className={classes.statusCell}>
        <Text span className={`${classes.pill} ${pill.cls}`}>{pill.text}</Text>
        <Text span className={classes.statusSub}>{sub}</Text>
      </Box>
    );
  };

  const stickyHead = grid.stickyActions;
  const showFraming = tab === "all" && !showError && !stmtLoading;

  const renderBody = () => {
    if (showError) {
      return (
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT} className={classes.stateCell}>
            <SettlementLoadError onRetry={reload} />
          </Table.Td>
        </Table.Tr>
      );
    }
    if (stmtLoading) {
      return Array.from({ length: 4 }).map((_, i) => (
        <Table.Tr key={`sk-${i}`}>
          <Table.Td className={stickyHead}>
            <Skeleton h={14} w={40} />
          </Table.Td>
          {Array.from({ length: COLUMN_COUNT - 1 }).map((__, j) => (
            <Table.Td key={j}>
              <Skeleton h={14} w={j === 2 ? 110 : 80} ml={j >= 3 && j <= 5 ? "auto" : undefined} />
            </Table.Td>
          ))}
        </Table.Tr>
      ));
    }
    const body = rows.map((r) => {
      const isAdd = r.direction === "ADD";
      const canExtend = isAdd && (r.overdueDays ?? 0) > 0;
      return (
        <Table.Tr key={r.id} className={classes.rowClickable} onClick={() => openInvoice(r.invoiceId)}>
          <Table.Td className={stickyHead} onClick={(e) => e.stopPropagation()}>
            <RowActions
              inlineOnly
              items={[
                {
                  key: "details",
                  intent: "view",
                  icon: <FileTextIcon size={17} />,
                  label: t("tooltip.details"),
                  onClick: () => openInvoice(r.invoiceId),
                },
                {
                  key: "extend",
                  intent: "edit",
                  icon: <CalendarPlusIcon size={17} />,
                  label: t("invoice:extendTooltip"),
                  onClick: () => openExtend(r.invoiceId),
                  hidden: !canExtend,
                },
              ]}
            />
          </Table.Td>
          <Table.Td className={grid.muted}>{ubFormat(r.createdAt, "MM-DD HH:mm")}</Table.Td>
          <Table.Td>
            <Link to={invoiceLink(r.invoiceId)} onClick={(e) => e.stopPropagation()} className={grid.docLink}>
              {r.invoice?.refCode ?? dash}
            </Link>
          </Table.Td>
          <Table.Td>
            <StatusBadge withDot={false} color={KIND_COLORS[r.logType]} label={t(`detail.kind.${r.logType}`, { defaultValue: t("detail.kind.OTHER") })} />
          </Table.Td>
          <Table.Td className={`${grid.num} ${isAdd ? grid.muted : grid.muted}`}>{isAdd ? money(r.amount) : "-"}</Table.Td>
          <Table.Td className={`${grid.num} ${isAdd ? grid.muted : grid.muted}`}>{isAdd ? "-" : money(r.amount)}</Table.Td>
          <Table.Td className={`${grid.num} ${classes.bold}`}>{money(r.balanceAfter)}</Table.Td>
          <Table.Td className={`${classes.dueCell} ${grid.muted}`}>{isAdd ? (r.dueDay ?? dash) : ""}</Table.Td>
          <Table.Td>{invoiceStatusCell(r)}</Table.Td>
        </Table.Tr>
      );
    });

    if (rows.length === 0) {
      return (
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT} className={grid.empty}>
            {t("detail.empty")}
          </Table.Td>
        </Table.Tr>
      );
    }

    return (
      <>
        {showFraming && (
          <Table.Tr className={grid.subRow}>
            <Table.Td className={stickyHead} />
            <Table.Td colSpan={5}>{t("detail.openingRow", { date: periodStart ?? dash })}</Table.Td>
            <Table.Td className={`${grid.num} ${classes.bold}`}>{money(data?.firstAmount)}</Table.Td>
            <Table.Td colSpan={2} />
          </Table.Tr>
        )}
        {body}
      </>
    );
  };

  if (!INV_SETTLEMENT_GET.hasAccess) {
    return (
      <Page>
        <PageHeader title={t("detail.title")} />
        {INV_SETTLEMENT_GET.accessDenied()}
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        align="center"
        breadcrumb={
          <Breadcrumb
            data={[
              { label: t("breadcrumb.home"), to: "/" },
              { label: t("breadcrumb.list"), to: "/settlement" },
              { label: refCode ?? dash },
            ]}
          />
        }
        title={
          <Box component="span" className={classes.titleRow}>
            <Text span inherit>
              {t("detail.titlePrefix")}{" "}
              <Text span inherit className={classes.titleMono}>
                {refCode ?? dash}
              </Text>
            </Text>
            {status && <StatusBadge color={STATUS_COLORS[status]} label={t(`status.${status}`, { defaultValue: status })} />}
          </Box>
        }
        extra={
          <Box className={classes.headerExtra}>
            <Box component="ol" aria-label={t("detail.progress")} className={classes.stepper}>
              {steps.map((s, i) => (
                <Box component="li" key={i} className={classes.step}>
                  <Box component="span" className={classes.stepDot} data-state={stepState(i)} />
                  <Box component="span" className={classes.stepText}>
                    <Text span className={classes.stepTitle} data-active={i <= currentStep || undefined}>
                      {s.title}
                    </Text>
                    <Text span className={classes.stepSub}>
                      {s.sub}
                    </Text>
                  </Box>
                  {i < steps.length - 1 && <Box component="span" className={classes.stepLine} />}
                </Box>
              ))}
            </Box>
            {action === "send" && (
              <Button
                loading={busy}
                onClick={onSend}
                disabled={!INV_SETT_SEND.hasAccess}
                color="brand"
                leftSection={<PaperPlaneTiltIcon size={16} weight="bold" />}
              >
                {t("detail.sendSettlement")}
              </Button>
            )}
            {action === "respond" && (
              <Button loading={busy} onClick={onRespond} disabled={!INV_SETTLEMENT_ACCEPT_PAYMENT.hasAccess} color="brand">
                {t("detail.approveSettlement")}
              </Button>
            )}
            {action === "pdf" && (
              <Button
                variant="default"
                loading={isPdfLoading(`${id}`)}
                disabled={!INV_SETTLEMENT_PAYMENT_PDF.hasAccess}
                onClick={() => downloadPdf(`${id}`)}
                leftSection={<FilePdfIcon size={16} />}
              >
                {t("tooltip.pdf")}
              </Button>
            )}
          </Box>
        }
      />

      <Box className={classes.party}>
        {loadedHeader ? (
          <>
            <Text span className={classes.partyAvatar}>
              {initials(renderBusinessName(business, ""))}
            </Text>
            {business?.id ? (
              <Anchor component={Link} to={`/customer-billing/${business.id}`} className={classes.partyName} underline="hover">
                {renderBusinessName(business, "-")}
              </Anchor>
            ) : (
              <Text span className={classes.partyName}>
                {renderBusinessName(business, "-")}
              </Text>
            )}
            <Text span className={classes.partyCode}>
              {[business?.refCode, business?.regNumber ? `${t("detail.regNumber")} ${business.regNumber}` : null].filter(Boolean).join(" · ")}
            </Text>
            <Text span className={classes.sep} aria-hidden>
              |
            </Text>
            <Text span>
              {periodStart ?? dash} - {periodEnd ?? dash}
            </Text>
            <Text span className={classes.sep} aria-hidden>
              |
            </Text>
            <Text span>
              {t("detail.finance")}:{" "}
              <Text span fw={500} c="gray.9">
                {renderName(data?.financeStaff)}
              </Text>
            </Text>
          </>
        ) : (
          <Skeleton h={20} w={420} />
        )}
      </Box>

      <Box component="section" aria-label={t("detail.flow.title")} className={classes.flow}>
        {flow.map((f, i) => (
          <Box key={i} className={classes.flowCell} data-total={f.total || undefined}>
            <Text span className={classes.flowLabel}>
              {f.label}
            </Text>
            {loadedHeader ? (
              <Text span className={classes.flowValue} data-muted={f.muted || undefined} data-total={f.total || undefined}>
                {f.value}
              </Text>
            ) : (
              <Skeleton h={26} w="70%" />
            )}
            <Text span className={classes.flowHint}>
              {loadedHeader ? f.hint : " "}
            </Text>
          </Box>
        ))}
      </Box>

      <Box className={grid.card}>
        <ListToolbar
          tabs={
            counts
              ? [
                  { value: "all", label: t("detail.tabs.all"), count: counts.all },
                  { value: "add", label: t("detail.tabs.add"), count: counts.add },
                  { value: "sub", label: t("detail.tabs.sub"), count: counts.sub },
                  { value: "returned", label: t("detail.tabs.returned"), count: counts.returned },
                  { value: "overdue", label: t("detail.tabs.overdue"), count: counts.overdue, tone: "danger" as const },
                ]
              : undefined
          }
          activeTab={tab}
          onTabChange={setTab}
          right={
            <Text span className={classes.toolbarHint}>
              {t("detail.toolbarHint")}
            </Text>
          }
        />

        <Box className={grid.wrap}>
          <Table className={`${grid.table} ${classes.tableMin}`} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={stickyHead} w={96}>
                  <Text className={grid.stickyActionsHead}>{t("col.actions")}</Text>
                </Table.Th>
                <Table.Th>{t("detail.col.date")}</Table.Th>
                <Table.Th>{t("detail.col.invoice")}</Table.Th>
                <Table.Th>{t("detail.col.kind")}</Table.Th>
                <Table.Th className={grid.num}>{t("detail.col.added")}</Table.Th>
                <Table.Th className={grid.num}>{t("detail.col.subtracted")}</Table.Th>
                <Table.Th className={grid.num}>{t("detail.col.balance")}</Table.Th>
                <Table.Th className={classes.dueCell}>{t("detail.col.dueDate")}</Table.Th>
                <Table.Th>{t("detail.col.invoiceStatus")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
            {showFraming && rows.length > 0 && (
              <Table.Tfoot>
                <Table.Tr className={grid.subRow}>
                  <Table.Td className={stickyHead} />
                  <Table.Td colSpan={3}>{t("detail.closingRow", { date: periodEnd ?? dash })}</Table.Td>
                  <Table.Td className={grid.num}>{money(data?.confirmedAmount)}</Table.Td>
                  <Table.Td className={grid.num}>{money(Number(data?.paidAmount ?? 0) + Number(data?.returnedAmount ?? 0))}</Table.Td>
                  <Table.Td className={grid.num}>{money(data?.lastAmount)}</Table.Td>
                  <Table.Td colSpan={2} />
                </Table.Tr>
              </Table.Tfoot>
            )}
          </Table>
        </Box>
      </Box>

      <InvoicePeekDrawer invoiceId={openInvoiceId} onClose={closeInvoice} onExtend={(inv) => openExtend(inv.id)} />

      <ExtendPaymentModal invoiceId={extendId} opened={extendOpened} onClose={extendHandlers.close} onSuccess={onExtended} />
    </Page>
  );
};
